/**
 * Service Packages — prepaid named bundles of N units of one or more salon
 * services. Salons create packages in Settings, sell them to clients (100%
 * upfront), and redeem units at checkout (the service keeps full price while a
 * redemption payment draws down the prepaid balance).
 *
 * Backend contract lives on the `core` microservice. UUIDs are strings.
 */

/**
 * v2: a definition is either an evergreen "package" or a date-windowed
 * "special" (a promotion that only sells inside its effective window).
 */
export type PackageKind = "package" | "special";

/**
 * v3: how the definition was authored. Both types round-trip their builder
 * inputs via `template_params`. Legacy bogo_free/bundle/custom rows were
 * migrated server-side and no longer exist.
 */
export type PackageTemplateType = "bogo_percent" | "simple_discount";

/**
 * Builder inputs per offer type. Shapes mirror the backend contract
 * exactly — see templateMath.ts for the derived items/price math.
 * All quantities may be fractional (2 decimals, min 0.01).
 */
export interface BogoPercentTemplateParams {
  buy_service_id: string;
  buy_qty: number;
  get_service_id: string;
  get_qty: number;
  /** 0 < percent_off <= 100. 100 means the "get" service is free. */
  percent_off: number;
}

export type SimpleDiscountType = "flat";

export interface SimpleDiscountTemplateParams {
  items: Array<{ salon_service_id: string; quantity: number }>;
  discount_type: SimpleDiscountType;
  discount_value: number;
}

/**
 * Loose read shape for backward-compat: legacy rows may still carry
 * `discount_type: "percent"`. Editor code converts these to flat on open;
 * new saves always emit flat.
 */
export interface LegacySimpleDiscountTemplateParams {
  items: Array<{ salon_service_id: string; quantity: number }>;
  discount_type: "flat" | "percent";
  discount_value: number;
}

export type TemplateParams =
  | BogoPercentTemplateParams
  | SimpleDiscountTemplateParams;

/**
 * One service line within a package definition.
 * `quantity` may be fractional (2 decimals) — e.g. 2.5 syringes.
 */
export interface ServicePackageItem {
  salon_service_id: string;
  service_name: string;
  quantity: number;
}

/** A package definition that a salon offers for sale. */
export interface ServicePackage {
  id: string;
  name: string;
  description?: string | null;
  price: number;
  validity_days?: number | null;
  is_purchasable_online: boolean;
  status: string;
  items: ServicePackageItem[];
  /** v2 — defaults to "package" for rows created before specials existed. */
  kind?: PackageKind;
  /** v3 — every row is one of the two builder types (DB migrated). */
  package_type?: PackageTemplateType;
  /** v3 — builder inputs; round-trip the editor. */
  template_params?: TemplateParams | null;
  /**
   * v3 — when true, clients pay the listed price with no tax added; the
   * salon absorbs the tax out of the price. Defaults to false.
   */
  omit_tax_collection?: boolean;
  /** 'YYYY-MM-DD'. Required (both) when kind === 'special'; null otherwise. */
  effective_start_date?: string | null;
  effective_end_date?: string | null;
}

/**
 * Remaining balance for a single service inside a client's owned package.
 * Balances may be fractional (2 decimals) — e.g. 1.5 syringes remaining.
 */
export interface ClientServicePackageBalance {
  salon_service_id: string;
  service_name: string;
  quantity_total: number;
  quantity_remaining: number;
}

/** A package owned by a client, with per-service remaining/total balances. */
export interface ClientServicePackage {
  id: string;
  name: string;
  status: string;
  purchased_dt: string | null;
  expires_at: string | null;
  services: ClientServicePackageBalance[];
}

/**
 * Request body for creating/updating a package definition.
 * `quantity` may be fractional (2 decimals, min 0.01).
 */
export interface ServicePackageItemInput {
  salon_service_id: string;
  quantity: number;
}

export interface CreateServicePackageRequest {
  name: string;
  description?: string | null;
  price: number;
  validity_days?: number | null;
  is_purchasable_online?: boolean;
  items: ServicePackageItemInput[];
  /** v2 — omitted defaults to "package" on the backend. */
  kind?: PackageKind;
  /** v3 — the builder type used to author the definition. */
  package_type?: PackageTemplateType;
  /** v3 — builder inputs; round-trip the editor. */
  template_params?: TemplateParams | null;
  /** v3 — omitted defaults to false (tax added on top at sale). */
  omit_tax_collection?: boolean;
  /**
   * 'YYYY-MM-DD'. Both REQUIRED when kind === 'special' (end >= start);
   * must be absent/null for kind === 'package' (backend 400s otherwise).
   */
  effective_start_date?: string | null;
  effective_end_date?: string | null;
}

/** Partial update — every field optional (mirrors create body shape). */
export type UpdateServicePackageRequest = Partial<CreateServicePackageRequest>;

/** Response from archiving (DELETE) a package. */
export interface ArchiveServicePackageResponse {
  id: string;
  archived: true;
}

/** Request body for selling a package to a client. */
export interface SellServicePackageRequest {
  client_id: string;
  service_package_id: string;
}

/** One tax-rate line applied to a line item (mirrors booked_services taxes). */
export interface LineItemTax {
  tax_rate_id: number;
  name: string;
  rate: number;
  taxable_amount: number;
  tax_amount: number;
  jurisdiction_level?: string;
  jurisdiction_name?: string;
}

/** Response from selling a package: a fresh unpaid transaction to check out. */
export interface SellServicePackageResponse {
  transaction_id: number;
  transaction_uuid: string;
  /** TAX-INCLUSIVE amount due at checkout. */
  amount: number;
  /** v3 — the package's listed (pre-tax) price. */
  pre_tax_price?: number;
  /** v3 — total sales tax included in `amount` (0 when omit_tax_collection). */
  tax_total?: number;
  /** v3 — per-rate tax breakdown. */
  taxes?: LineItemTax[];
  package: ServicePackage;
}

/** One banked service to redeem from a package (used in the batch shape). */
export interface RedeemPackageItem {
  /** Salon service UUID. */
  salon_service_id: string;
  /** May be fractional (2 decimals, min 0.01). */
  quantity: number;
  /** Booked-service UUID, when redeeming against a specific line. */
  booked_service_id?: string;
}

/**
 * Request body for redeeming units of an owned package at checkout.
 *
 * Preferred shape sends a `redemptions` array so several banked services in
 * one package redeem atomically in a single call. The legacy scalar fields
 * (`salon_service_id`/`quantity`/`booked_service_id`) remain accepted by the
 * backend for back-compat.
 */
export interface RedeemPackageRequest {
  /** Transaction UUID. */
  transaction_id: string;
  client_service_package_id: string;
  /** Multi-service (preferred) — one entry per banked service to redeem. */
  redemptions?: RedeemPackageItem[];
  /** Legacy single-service fields (still accepted; prefer `redemptions`). */
  salon_service_id?: string;
  quantity?: number;
  booked_service_id?: string;
}

export interface RedeemPackagePayment {
  id?: string;
  uuid?: string;
  method?: string;
  type?: string;
  status?: string;
  amount?: number;
}

/** Per-line result of a redemption (one entry per redeemed banked service).
 *  Units-based: a redemption records no cash value and creates no payment row —
 *  it reduces the covered line's remaining cost via the backend triggers. */
export interface RedeemPackageLineResult {
  quantity_redeemed: number;
  quantity_remaining: number;
  client_service_package_status: string;
}

export interface RedeemPackageResponse {
  transaction: {
    uuid: string;
    status: string;
    appointment_status: string;
  };
  payments: RedeemPackagePayment[];
  /** Multi-service result — one entry per redeemed line. */
  redemptions?: RedeemPackageLineResult[];
  /** Back-compat singular (== redemptions[0]). */
  redemption: RedeemPackageLineResult;
}

// ===========================================================================
// Manual package management — adjustments (grant / adjust bank / edit expiry /
// revoke). Salon staff manage a client's owned packages from the client
// profile; every mutation writes an immutable `ClientPackageAdjustment` audit
// row. Backend contract lives on the `core` microservice.
// ===========================================================================

/** The four kinds of manual adjustment recorded against a client's package. */
export type ClientPackageAdjustmentType =
  | "grant_package"
  | "adjust_bank"
  | "edit_expiry"
  | "revoke_package";

/**
 * The single shared reason taxonomy attached to every manual adjustment. Used
 * by all four actions (grant / adjust / edit-expiry / revoke) and the read-only
 * history view.
 */
export type PackageReasonCategory =
  | "goodwill"
  | "correction"
  | "comp"
  | "data_migration"
  | "other";

/**
 * Value/label pairs for the shared reason picker. Order is the display order in
 * the reason `Select`.
 */
export const PACKAGE_REASON_CATEGORIES: {
  value: PackageReasonCategory;
  label: string;
}[] = [
  { value: "goodwill", label: "Goodwill" },
  { value: "correction", label: "Correction" },
  { value: "comp", label: "Comp" },
  { value: "data_migration", label: "Data Migration" },
  { value: "other", label: "Other" },
];

/**
 * Revoke offers a narrower reason set than the other actions — goodwill and comp
 * make no sense when forfeiting a client's remaining units. Mirrors the backend
 * VALID_REVOKE_REASON_CATEGORIES. Derived from the shared list to keep labels
 * and order in lockstep.
 */
export const REVOKE_REASON_CATEGORIES = PACKAGE_REASON_CATEGORIES.filter((c) =>
  ["correction", "data_migration", "other"].includes(c.value),
);

/**
 * One immutable audit row describing a manual change to a client's owned
 * package. Returned by GET
 * `/business-locations/{salonId}/client-service-packages/{cspId}/adjustments`.
 *
 * `*_before` / `*_after` snapshot the affected quantity or expiry around the
 * change; `delta_total` / `delta_remaining` are signed unit deltas (positive =
 * added to the bank, negative = removed/forfeited). `service_name` is joined
 * and present for `adjust_bank` (the per-service line being adjusted).
 */
export interface ClientPackageAdjustment {
  id: string;
  adjustment_type: ClientPackageAdjustmentType;
  salon_service_id: number | null;
  service_name: string | null;
  delta_total: number;
  delta_remaining: number;
  total_before: number | null;
  total_after: number | null;
  remaining_before: number | null;
  remaining_after: number | null;
  expiry_before: string | null;
  expiry_after: string | null;
  reason_category: PackageReasonCategory | null;
  note: string;
  created_dt: string;
  created_by: string;
  created_by_name: string | null;
}

/**
 * Fields shared by every manual adjustment request. Both are optional on
 * package adjustments: `reason_category` may be `null` and `note` may be an
 * empty string. Persisted on the audit ledger for whichever value(s) were
 * provided.
 */
export interface PackageAdjustmentReason {
  reason_category: PackageReasonCategory | null;
  note: string;
}

/** Request body for granting a package to a client (future slice). */
export interface AddPackageToClientRequest extends PackageAdjustmentReason {
  service_package_id: string;
}

/**
 * Request body for adjusting a client's remaining/total unit bank (future
 * slice). At least one of `quantity_total` / `quantity_remaining` is supplied.
 */
export interface AdjustBankRequest extends PackageAdjustmentReason {
  /**
   * Signed unit delta applied to BOTH the bank's total and remaining. Positive
   * adds units, negative removes them; must be non-zero (≤ 2 decimal places).
   */
  quantity_delta: number;
}

/**
 * Request body for editing a client package's expiry (future slice).
 * `expires_at` is an ISO timestamp, or `null` to clear the expiry.
 */
export interface EditExpiryRequest extends PackageAdjustmentReason {
  expires_at: string | null;
}

/** Request body for revoking a client's package (future slice). */
export type RevokePackageRequest = PackageAdjustmentReason;

// ---------------------------------------------------------------------------
// Manual adjustment RESPONSE shapes (grant / adjust bank / edit expiry /
// revoke). Every mutation returns the immutable `adjustment_id` it wrote plus
// the affected package/balance snapshot. Callers refetch the client's packages
// after a success, so these are typed for correctness rather than consumed to
// render.
// ---------------------------------------------------------------------------

/**
 * One per-service balance as returned inline on the grant response. Mirrors
 * `ClientServicePackageBalance` minus the joined `service_name`.
 */
export interface ClientServicePackageBalanceLite {
  salon_service_id: string;
  quantity_total: number;
  quantity_remaining: number;
}

/** Response from granting a package to a client. */
export interface AddPackageToClientResponse {
  package: {
    id: string;
    name: string;
    price: number;
    kind?: PackageKind;
    expires_at: string | null;
    balances: ClientServicePackageBalanceLite[];
  };
  adjustment_id: string;
}

/** Response from adjusting a single service bank's total/remaining. */
export interface AdjustBankResponse {
  balance: ClientServicePackageBalance;
  adjustment_id: string;
  client_service_package_status: string;
}

/** Response from editing a client package's expiry. */
export interface EditExpiryResponse {
  expires_at: string | null;
  adjustment_id: string;
  client_service_package_status: string;
}

/** Response from revoking a client's package. */
export interface RevokePackageResponse {
  adjustment_id: string;
  forfeited_units: number;
  client_service_package_status: string;
}
